import * as Sentry from '@sentry/vue';
import { browserTracingIntegration } from '@sentry/vue';
import { Env } from '/@/Env';
import { HttpStatus } from '@/modules/core/app/constants/api.constants';

class SentryService {
  /**
   * @return {boolean}
   */
  get isEnabled() {
    return Env.nodeEnv().VITE_APP_SENTRY_ENABLED ?? false;
  }

  /**
   * @param app
   */
  init(app) {
    if (!this.isEnabled) {
      return;
    }

    Sentry.init({
      app,
      environment: Env.nodeEnv().MODE,
      release: `${Env.nodeEnv().MODE}-${Env.getAppVersion()}`,
      dsn: Env.nodeEnv().VITE_APP_SENTRY_DSN,
      integrations: [browserTracingIntegration()],
      // We recommend adjusting this value in production, or using tracesSampler
      // for finer control
      // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
      // The previous regex /^https:\/\/api\..*\.clearestate\.com\/api/ only matches URLs with a subdomain between 'api.' and '.clearestate.com', and must have '/api' after the domain.
      // To match 'https://api.clearestate.com', 'https://api.uat.clearestate.com' etc., for all API endpoints (with or without a subdomain), use:
      tracePropagationTargets: ['localhost', /^https:\/\/api(\.[^\.]+)?\.clearestate\.com/],
      tracesSampleRate: 0.25,
      beforeSend: this.beforeSend
    });
  }

  /**
   * Prevents from sending any errors other than 500 to Sentry
   */
  beforeSend(event, hint) {
    if (hint.originalException?.status !== HttpStatus.INTERNAL_SERVER_ERROR) {
      return null;
    }

    return event;
  }

  /**
   * Context is viewable on the issue page, while tags and user information are filterable, hence some duplication
   * Also unlike the backend AuthUser contains currentEstate so it's unnecessary to add that context
   * @param {AuthUser} user
   */
  setContext(user) {
    if (!this.isEnabled) {
      return;
    }

    Sentry.setUser({
      id: user?.id,
      email: user?.email
    });
    Sentry.setContext('user', user);
    Sentry.setTag('estate', user?.currentEstate?.id);
    Sentry.setTag('estate.name', user?.currentEstate?.contact?.fullName);
    Sentry.setTag('estate.type', user?.currentEstate?.type);
    Sentry.setTag('role', user?.estateAccount?.roleId);
    Sentry.setTag('role.name', user?.estateAccount?.estateRole?.name);
    Sentry.setTag('jurisdiction', user?.currentEstate?.jurisdiction?.name);
    Sentry.setTag('user.email', user?.email);
    Sentry.setTag('user.name', user?.contact?.fullName);
  }

  captureException(error) {
    if (!this.isEnabled) {
      return;
    }

    const captureContext = {};
    const extra = error.getSentryExtra?.();

    if (extra) {
      captureContext.extra = extra;
    }

    Sentry.captureException(error, captureContext);
  }
}

export default new SentryService();
